import find from 'lodash/find';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import React, { FC, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import set from 'lodash/set';
import get from 'lodash/get';

import FormContext from 'src/presentation/contexts/FormContext';
import FieldContainer from './FieldContainer';
import TextInput from '@ad-scripts/presentation/components/common/text_input';
import getErrorMessage from './getErrorMessage';

const StyledInput = styled(TextInput)`
  flex: 1 1 auto;
  height: 32px;
  width: 100%;

  ::placeholder {
    color: ${p => p.theme.colors.gray500};
  }

  ${({ type }) =>
    type === 'number' &&
    `
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `};
`;

export enum InputType {
  string,
  number,
}

type InputProps = {
  'data-aid'?: string;
  attr: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  description?: string | JSX.Element;
  label?: string | JSX.Element;
  placeholder?: string;
  readOnly?: boolean;
  type?: InputType;
  onChange?: Function;
  value?: string;
  maxLength?: number;
};

/*
@deprecated Since TextInput is deprecated, use ./InputField.tsx instead
*/
const Input: FC<InputProps> = ({
  attr,
  autoFocus,
  disabled,
  description,
  label,
  onChange,
  value,
  placeholder,
  readOnly,
  'data-aid': dataAid = null,
  className,
  type = InputType.string,
  maxLength = undefined,
}) => {
  const [valueState, setValueState] = useState('');

  const { errors, workingState, updateState, resetError, onBlur } = useContext(FormContext);
  const error = find(errors, error => error.attr === attr);

  const onInputChange = useCallback(
    ev => {
      let val = ev.target.value;

      if (type === InputType.number) {
        const [value, isValid] = getNumberValue(val);
        if (!isValid) {
          return false;
        }
        setValueState(ev.target.value);
        ev.target.value = val = value;
      }

      if (onChange) {
        onChange(ev);
      } else {
        resetError({ attr, errors });
        updateState(set({}, attr, isNil(val) ? '' : val));
      }
    },
    [attr, errors, onChange, resetError, type, updateState]
  );

  const getNumberValue = (value?: string): [number | undefined, boolean] => {
    if (!value) {
      return [undefined, true];
    }
    let val = value.trim();
    if ((val.length === 1 && ['+', '-'].includes(val)) || val.endsWith('.')) {
      val += '0';
    }
    if (/^[+-]?\d+(\.\d+)?$/.test(val)) {
      return [parseFloat(val), true];
    }

    return [undefined, false];
  };

  const convertValue = (value?: string | number): string => {
    if (isNumber(value) && isFinite(value)) return `${value}`;
    return value ? (value as string) : '';
  };

  let workingVal = value != undefined ? value : get(workingState, attr, '');

  // @ts-ignore
  if (valueState && parseFloat(valueState!) === workingVal) {
    workingVal = valueState;
  }
  workingVal = convertValue(workingVal); // Make sure we do not send 0.00 as a number
  return (
    <FieldContainer
      error={error ? getErrorMessage(error, label) : undefined}
      label={label}
      description={description}
      data-aid={dataAid}
      control={
        <StyledInput
          className={error ? `${className} invalid` : className}
          data-aid={dataAid}
          autoFocus={autoFocus}
          disabled={disabled}
          isError={!!error}
          onChange={onInputChange}
          onBlur={onBlur}
          placeholder={placeholder}
          readOnly={readOnly}
          value={workingVal}
          maxLength={type === InputType.number && !maxLength ? 20 : maxLength}
          type="text"
        />
      }
    />
  );
};

export default Input;
